import React, { useEffect } from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import { Route, Router, matchPath } from 'react-router-dom';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import RepAI from '@repai/widget';
import { QueryParamProvider } from 'use-query-params';
import { store, history, useAppSelector } from 'shared/store';
import { initStore } from 'shared/store/init';
import log from 'shared/utils/log';
import { initSentry } from 'shared/utils/sentry';
import Routes, { routes } from './Routes';
import ErrorWrap from 'shared/components/ErrorWrap';
import { selectActiveUser } from 'shared/store/auth-slice';
import { disableReactDevToolsInProduction } from 'shared/utils/helpers';
import { getProtocolVersion, getCommitHash } from 'sb-lib/env';
import { RealtimeManager } from 'shared/realtime';
import { SelfUser } from 'types/user';
import { PAYMENT_PLAN } from 'types/payment';
import { selectAuthedAccount } from 'shared/store/shared-selectors';
import 'shared/utils/camefrom'; // Prime this for later use
import { GlobalInbox } from './components/GlobalInbox';
import { AppNotificationsModal } from './components/AppNotificationsModal';
import { CallLogModal } from './components/CallLogModal';
import { ChatUsageWarningLimitModal } from 'browser/components/Dashboard/UsageWarningModals/ChatUsageWarningLimitModal';
import { useSvgSprite } from 'shared/components/Icon';
import { OutboundProvider } from './components/Outbound/outbound-provider';
import { OutboundActiveCall } from './components/OutboundActiveCall';
import { OutboundLiveListenForwarder } from './components/OutboundLiveListenForwarder';
import { LiveListenProvider } from './components/LiveListen/livelisten-provider';
import { VideoRoomProvider } from 'shared/contexts/video-room';
import { LiveListenActiveCall } from './components/LiveListenActiveCall';
import { register } from 'extendable-media-recorder';
import { connect } from 'extendable-media-recorder-wav-encoder';
import { message } from 'antd';

disableReactDevToolsInProduction();

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register(`/service-worker.js`).then(reg => {
    log.debug('Service worker registered', reg);
  });
}
const activeRoute = routes.find(config =>
  matchPath(window.location.pathname, config.route),
);
const isAgentPage = activeRoute?.meta?.isAgentPage;

initStore(store, isAgentPage);
initSentry(store);

if (process.env.SERVICEBELL_CLIENT_KEY) {
  if (!window.location.pathname.startsWith('/zendesk') && !isAgentPage) {
    RepAI('init', process.env.SERVICEBELL_CLIENT_KEY, {
      mode: 'retrigger',
    });

    let prevUser: SelfUser | null = null;
    store.subscribe(() => {
      const state = store.getState();
      const account = selectAuthedAccount(state);
      const user = selectActiveUser(state);
      if (account && user) {
        // Only call identify again if the values actually changed
        const needsReidentification =
          user !== prevUser &&
          (user.id !== prevUser?.id || user.displayName !== prevUser?.displayName);
        if (needsReidentification) {
          // TODO: Remove any cast after module is updated to 0.1.2
          (RepAI as any)(
            'identify',
            account.id,
            {
              email: account.emailAddress,
              displayName: user.displayName,
              tag: user.org.plan.kind === PAYMENT_PLAN.FREE ? '' : 'PAID_PLAN',
              // Custom keys
              orgName: user.org.name || '',
              orgId: user.org.id,
              orgRoles: user.orgRoles.join(', '),
            },
            account.secureIdentityHash,
          );
        }
      }
      prevUser = user;
    });
  }
}

const App = () => {
  useSvgSprite();

  useEffect(() => {
    message.config({ maxCount: 1 });

    const setupMediaRecorder = async () => {
      await register(await connect());
    };

    setupMediaRecorder();
  }, []);

  return (
    <ErrorWrap isFullscreen>
      <GoogleReCaptchaProvider reCaptchaKey={process.env.RECAPTCHA_SITE_KEY}>
        <Provider store={store}>
          <Router history={history}>
            <QueryParamProvider ReactRouterRoute={Route}>
              {!isAgentPage && <RealtimeManager />}
              <OutboundProvider>
                <VideoRoomProvider>
                  <LiveListenProvider>
                    <Routes />
                    <OutboundLiveListenForwarder />
                    <LiveListenActiveCall />
                  </LiveListenProvider>
                </VideoRoomProvider>
                <OutboundActiveCall />
              </OutboundProvider>
              <AuthProtectedComponents />
            </QueryParamProvider>
          </Router>
        </Provider>
      </GoogleReCaptchaProvider>
    </ErrorWrap>
  );
};

const AuthProtectedComponents = () => {
  const user = useAppSelector(selectActiveUser);

  if (!user || isAgentPage) return <></>;

  return (
    <>
      <GlobalInbox />
      <AppNotificationsModal />
      <CallLogModal />
      <ChatUsageWarningLimitModal />
    </>
  );
};

log.debug(
  `Running app from commit ${getCommitHash()}, protocol version ${getProtocolVersion()}`,
);
render(<App />, document.getElementById('app'));
